import {
  ExclamationCircleOutlined,
  PlusOutlined,
  WarningFilled,
} from '@ant-design/icons';
import {
  Button,
  CollapsibleContentContainer,
  Heading,
  Input,
  Option,
  Select,
  Tooltip,
} from '@mm/components';
import PrimarySelect, {
  DropdownOption,
} from '@mm/components/legacy-components/PrimarySelect/PrimarySelect';
import { useConnectOauthToken } from '@mm/hooks';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { SyncIcon } from '@mm/icons';
import { GoogleAPI } from '@mm/plugins';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import {
  blankVariable,
  getEmailAccountsWithErrors,
} from '@mm/state/helpers/connection-helpers';
import { normalizeVariableName } from '@mm/state/helpers/variable-names-helper';
import {
  GoogleAccount,
  GoogleSpreadsheet,
} from '@mm/state/interfaces/googleSheet';
import AttributeModel from '@mm/state/model/AttributeModel';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { selectConnectionsGoogleAccounts } from '@mm/state/modules/connectionsTableState/selectors';
import { IndexAttributeNameMap } from '@mm/state/modules/modalsState/props';
import { selectAttributes } from '@mm/state/selectors/attributeSelectors';
import { selectActiveEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import {
  selectGoogleAccounts,
  selectSheetColumns,
  selectSheets,
  selectSpreadsheets,
} from '@mm/state/selectors/googleSheetSelectors';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { colors, getUniqId } from '@mm/utils';
import { Alert, ConfigProvider, Divider } from 'antd';
import { ReactComponent as ArrowRightLongIcon } from 'assets/icons/arrow-right-long.svg';
import classNames from 'classnames';
import { unionBy } from 'lodash';
import React, { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AudienceSimpleSelect } from '../AudienceSimpleSelect/AudienceSimpleSelect';
import styles from './GoogleSheetsIntegrationEditor.module.css';

type GoogleSheet = {
  id: number;
  title: string;
};

interface Props {
  setConnection?: (connection: ConnectionModel) => void;
  connection?: ConnectionModel;
  showConnectionWidgetInfo?: boolean;
  showConnectionNameAndActivation?: boolean;
  withTriggerOption?: boolean;
}

export const GoogleSheetsIntegrationEditor: React.FunctionComponent<
  Props
  // tslint:disable-next-line:cyclomatic-complexity
> = props => {
  const {
    connection,
    setConnection,
    showConnectionWidgetInfo,
    showConnectionNameAndActivation,
    withTriggerOption,
  } = props;
  const emailAccounts = useSelector(selectActiveEmailAccounts);
  const isShopify = useSelector(selectIsShopifyContext);

  const accounts = useSelector(selectGoogleAccounts);
  const connectionsGoogleAccounts = useSelector(
    selectConnectionsGoogleAccounts,
  );
  const audiences = useSelector(selectAudienceSorted);
  const selectedAudience = audiences.find(a => a.id === connection?.audienceId);

  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const { update } = useConnectionCRUDActions();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const [connectionName, setConnectionName] = useState(connection?.name || '');

  const { pixelName = 'X-Ray Pixel', companyName = 'Customers.ai' } =
    useWhitelabelConfig();

  const xrayPromoterOptions = useSelector((state: StoreState) =>
    (state.promoter.entities || [])
      .filter(p => p?.botId === chatbotId && p.type === 'getemails')
      .map(
        p =>
          ({
            id: p.id as number,
            label: p.name,
          } as Option<number>),
      ),
  );
  const selectedXrayPromoterOptions = xrayPromoterOptions.filter(
    o => o.id && (connection?.promoterIds || []).includes(o.id),
  );

  const allAccounts = unionBy(accounts, connectionsGoogleAccounts, 'id');
  const filtertedAccounts = accounts.filter(acc => !acc.readOnly);
  const spreadsheets = useSelector(selectSpreadsheets);
  const sheets = useSelector(selectSheets);
  const [selectedAccount, setSelectedAccount] = useState<GoogleAccount>();
  const [accountNotAuthorized, setAccountNotAuthorized] = useState(false);

  const [selectedSpreadsheet, setSelectedSpreadsheet] =
    useState<GoogleSpreadsheet>();
  const [selectedSheet, setSelectedSheet] = useState<GoogleSheet>();
  const [selectedLookupColumn, setSelectedLookupColumn] =
    useState<DropdownOption>();
  const [selectedLookupValue, setSelectedLookupValue] =
    useState<AttributeModel>();
  const { getAccounts, getSheets, getColumns, getAccessToken, getOauthUrl } =
    useGoogleSheetsCRUDActions();
  const { connect: launchOauthPopup } = useConnectOauthToken(
    'googleOAuth',
    'googleAccountConnected',
    () => getOauthUrl(),
    async () => {
      const accounts = await getAccounts(activeFbPageId, {
        sortByUpdatedAtDesc: true,
      });
      setAccountNotAuthorized(false);

      if (accounts && accounts.length > filtertedAccounts.length) {
        setSelectedAccount(accounts[0]);
      }
    },
  );

  const columns = useSelector(selectSheetColumns);
  const isReadOnlyAccount = !!accounts.find(
    acc => acc.id === connection?.googleAccountId && acc.readOnly,
  );
  const mapFiltered = columns.map(column => {
    const variableColumn = connection?.variables?.find(
      variable => variable.sentAs === column.name,
    );
    if (variableColumn) {
      return variableColumn;
    }
    return {
      sentAs: column.name,
      name: '',
    };
  });
  const mappedVars = connection?.variables?.map(variable => ({
    sentAs: variable.sentAs,
    name: variable.name,
  }));
  const mappings =
    mapFiltered.length && !isReadOnlyAccount ? mapFiltered : mappedVars;

  const isMyAccount = !!(
    connection?.googleAccountId &&
    connection?.googleAccountEmail &&
    accounts.find(
      account =>
        account.id === connection.googleAccountId &&
        account.email === connection.googleAccountEmail,
    )
  );

  const accountOptions = [
    { id: 0, email: 'New Account' },
    ...filtertedAccounts,
  ];

  const customOptionRenderer = (option: DropdownOption) => {
    if (option.id === 0) {
      return (
        <span>
          <PlusOutlined /> {option.email}
        </span>
      );
    }

    return <span>{option.email}</span>;
  };

  const googleSheetTitle = connection?.googleSheetTitle ?? '';

  useEffect(() => {
    if (connection?.googleAccountId) {
      const currAccount = allAccounts.find(
        account => account.id === connection.googleAccountId,
      );
      if (currAccount?.id && currAccount?.email) {
        setSelectedAccount(currAccount as GoogleAccount);
      }
    }
    if (isMyAccount) {
      if (
        connection?.googleSpreadsheetId &&
        connection.googleSpreadsheetTitle
      ) {
        setSelectedSpreadsheet({
          id: connection.googleSpreadsheetId,
          name: connection.googleSpreadsheetTitle,
        });
      } else {
        setSelectedSpreadsheet(undefined);
      }
      if (connection?.googleSheetId) {
        setSelectedSheet(
          sheets.find(sheet => sheet.id === Number(connection.googleSheetId)),
        );
      } else {
        setSelectedSheet(undefined);
      }
    }
    if (connection?.googleLookupColumn) {
      if (columns) {
        setSelectedLookupColumn(
          columns.find(column => column.id === connection.googleLookupColumn),
        );
      }
      setSelectedLookupColumn({
        id: connection.googleLookupColumn,
        name: connection.googleLookupColumn,
      });

      setSelectedLookupValue(
        connection.variables?.find(
          variable => variable.sentAs === connection.googleLookupColumn,
        ),
      );
    } else {
      setSelectedLookupColumn(undefined);
      setSelectedLookupValue(undefined);
    }
  }, [connection, sheets, spreadsheets]);

  const updateConnection = async (updatedFields: Partial<ConnectionModel>) => {
    if (!connection) {
      return;
    }
    const updatedConnection = await update(activeFbPageId, {
      ...connection,
      validationMessages: null,
      ...updatedFields,
    });

    if (updatedConnection) {
      setConnection?.(updatedConnection);
    }
  };

  const getVariablesToUpdate = (name: string, sentAs: string) => {
    if (connection?.variables) {
      const idx = connection.variables.findIndex(
        variable => variable.sentAs === sentAs || variable.name === name,
      );

      const variables = [...connection.variables];
      const variableToSet = {
        ...blankVariable(),
        name,
        sentAs,
      };

      if (idx === -1) {
        return [variableToSet, ...variables];
      }

      variables[idx] = variableToSet;
      return variables;
    }
  };
  const updateVariables = (name: string, sentAs: string) => {
    updateConnection({
      variables: getVariablesToUpdate(name, sentAs),
    });
  };

  const updateAudience = (audienceId: number) => {
    updateConnection({
      audienceId,
    });
  };

  const onSelectAccount = (item: GoogleAccount) => {
    if (item.id === 0) {
      launchOauthPopup();
      return;
    }
    setAccountNotAuthorized(false);
    setSelectedAccount(item);
    updateConnection({
      googleAccountId: item.id,
      googleAccountEmail: item.email,
      googleSpreadsheetId: null,
      googleSpreadsheetTitle: null,
      googleSheetId: null,
      googleSheetTitle: null,
      variables: [],
      googleLookupColumn: '',
    });
    setSelectedSpreadsheet(undefined);
    setSelectedSheet(undefined);
  };

  const showPicker = async () => {
    if (!selectedAccount) {
      return;
    }
    let token;
    try {
      token = await getAccessToken(activeFbPageId, selectedAccount.id, true);
    } catch (e) {
      if (e.status === 401) {
        setAccountNotAuthorized(true);
      }
    }

    if (token) {
      GoogleAPI.showPicker(token, (id, name) => {
        setSelectedSpreadsheet({ id, name });
        getSheets(selectedAccount.id, id);
        updateConnection({
          googleSpreadsheetId: id,
          googleSpreadsheetTitle: name,
          googleSheetId: null,
          googleSheetTitle: null,
          variables: [],
          googleLookupColumn: '',
        });
      });
    }
  };

  const onSelectSheet = async (item: GoogleSheet) => {
    setSelectedSheet(item);
    await updateConnection({
      googleSheetId: String(item.id),
      googleSheetTitle: item.title,
      variables: [],
      googleLookupColumn: '',
    });
    if (
      connection &&
      connection.googleAccountId &&
      connection.googleSpreadsheetId
    ) {
      await getColumns(
        connection.googleAccountId,
        connection.googleSpreadsheetId,
        item.title,
      );
    }
  };

  const onSelectLookupColumn = (item: DropdownOption) => {
    const variable = connection?.variables?.find(
      variable => variable.sentAs === selectedLookupColumn?.name,
    );
    if (variable && item.name) {
      updateConnection({
        googleLookupColumn: item.name,
        variables: getVariablesToUpdate(variable.name, item.name),
      });
    } else {
      updateConnection({
        googleLookupColumn: item.name,
      });
    }
    setSelectedLookupColumn(item);
  };

  const onSelectLookupValue = (item: AttributeModel) => {
    setSelectedLookupValue(item);
    if (selectedLookupColumn?.name) {
      updateVariables(item.name, selectedLookupColumn.name);
    }
  };

  const onClickRefresh = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (
      connection &&
      connection.googleAccountId &&
      connection.googleSpreadsheetId &&
      connection.googleSheetTitle
    ) {
      getColumns(
        connection.googleAccountId,
        connection.googleSpreadsheetId,
        connection.googleSheetTitle,
      );
    }
  };

  const [map, setMap] = useState<IndexAttributeNameMap>({});
  const attributes = useSelector(selectAttributes);

  const withPhone = attributes.some(({ name }) => name === 'PHONE');
  const attrs = !withPhone
    ? [
        ...attributes,
        {
          id: getUniqId(),
          name: 'PHONE',
          system: false,
        },
      ]
    : attributes;

  const selectedNames = Object.values(map);
  const options = attrs
    .filter(attr => !selectedNames.includes(attr.name))
    .filter(attr => attr.name !== selectedLookupValue?.name);

  const onAttributeSelected = (
    { name }: AttributeModel,
    sentAs: string,
    index: number,
  ) => {
    const next = { ...map, [index]: name };
    setMap(next);

    updateVariables(name, sentAs);
  };
  const onSelectPromoters = (promoterOptions: Option<number>[]) => {
    updateConnection({
      promoterIds: promoterOptions.map(({ id }) => id),
    });
  };

  const [searchColumn, setSearchColumn] = useState('');

  const columnOptions = useMemo(
    () =>
      options
        .map(v => ({ ...v, label: v.friendlyName || v.name, value: v.id }))
        .filter(v =>
          searchColumn
            ? (v?.friendlyName || v?.name)
                ?.toLowerCase()
                .includes(searchColumn.toLowerCase())
            : true,
        ),
    [searchColumn, options],
  );

  const [searchSheet, setSearchSheet] = useState('');

  const sheetsOptions = useMemo(
    () =>
      sheets
        .map(v => ({ ...v, label: v.title, value: v.id }))
        .filter(v =>
          searchSheet
            ? (v?.label || v?.title)
                ?.toLowerCase()
                .includes(searchSheet.toLowerCase())
            : true,
        ),
    [searchSheet, sheets],
  );

  return (
    <>
      <CollapsibleContentContainer
        ordinal="1"
        title="Connection Settings"
        color={colors.green7}
      >
        <div className={styles.content}>
          <div className={styles.section}>
            {showConnectionNameAndActivation && (
              <>
                <Heading className={styles.selectHeading} size={5}>
                  Connection Name
                </Heading>
                <Input
                  css={{
                    container: styles.inputContainer,
                    input: styles.input,
                  }}
                  value={connectionName}
                  onChange={e => setConnectionName(e.target.value)}
                  onBlur={e => updateConnection({ name: e.target.value })}
                  placeholder="Enter a name for this connection"
                />
              </>
            )}
            {showConnectionWidgetInfo && !isShopify ? (
              <ConfigProvider
                theme={{
                  token: {
                    colorInfoBg: '#F0F5FF',
                    colorInfoBorder: '#85A5FF',
                  },
                  components: {
                    Alert: {
                      withDescriptionIconSize: 18,
                      withDescriptionPadding: '8px 12px',
                    },
                  },
                }}
              >
                <Alert
                  className={styles.bigAlert}
                  type="info"
                  showIcon
                  icon={<ExclamationCircleOutlined />}
                  message="Data Source"
                  description={`This integration can receive data from the data sources selected
                below, or can also be triggered by a connection widget in an
                Automation or dialogue.`}
                />
              </ConfigProvider>
            ) : (
              <Heading
                className={styles.section_name}
                size={4}
                text="Data Source"
              />
            )}

            <Heading className={styles.selectHeading} size={5}>
              Choose Audience to send data from
            </Heading>
            <AudienceSimpleSelect
              options={audiences}
              className={styles.select}
              placeholder=""
              selectedAudienceId={connection?.audienceId || undefined}
              onChange={audienceId => updateAudience(audienceId)}
            />
            {(selectedAudience?.contactsCount || 0) > 1000 && (
              <Alert
                className={styles.smallAlert}
                type="warning"
                showIcon
                message={`Only new contacts added to the audience will be synced
                automatically. To include past leads, please export them and add
                them manually.`}
              />
            )}

            {withTriggerOption && (
              <>
                <span className={styles.optional} style={{ margin: '16px 0' }}>
                  OR
                </span>
                <Heading
                  className={classNames(styles.selectHeading, styles.optional)}
                  size={5}
                >
                  Choose {pixelName} trigger to send data from
                </Heading>
                <Select
                  className={styles.select}
                  options={xrayPromoterOptions}
                  selected={selectedXrayPromoterOptions}
                  onChange={selectedPromoters =>
                    onSelectPromoters(selectedPromoters)
                  }
                  hideClearButton
                />
              </>
            )}
          </div>
          <Divider className={styles.divider} />
          <div className={styles.section}>
            <Heading
              size={4}
              className={styles.section_name}
              text="Set up your worksheet"
            />
            <div className={styles.select_container}>
              <div className={styles.account_dropdown_container}>
                <Heading
                  size={5}
                  className={classNames(
                    styles.selectHeading,
                    styles.selectHeadingRow,
                  )}
                >
                  Choose an account
                  {!!selectedAccount?.id &&
                    !!selectedAccount.email &&
                    getEmailAccountsWithErrors(emailAccounts || [])?.find(
                      v => v.emailAddress === selectedAccount.email,
                    ) && (
                      <Tooltip title="Please check your account configuration and permissions or reconnect">
                        <ExclamationCircleOutlined
                          style={{ color: '#ff4d4f' }}
                        />
                      </Tooltip>
                    )}
                </Heading>

                <PrimarySelect
                  className={styles.dropdown}
                  placeholder="Choose an account"
                  options={accountOptions}
                  customOptionRenderer={customOptionRenderer}
                  value={selectedAccount}
                  onChange={item => onSelectAccount(item as GoogleAccount)}
                  displayNameKey="email"
                  dropdownClassName={styles.dropdown_wrapper}
                  customDropdownStyles={{ zIndex: '1000003' }}
                  disabled={isReadOnlyAccount}
                />
                {accountNotAuthorized && (
                  <div className={styles.error}>
                    <span>Account is not authorized</span>
                    <Button onClick={() => launchOauthPopup()}>
                      Reauthorize
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <Heading
              size={5}
              className={styles.selectHeading}
              text="Choose Spreadsheet"
            />
            <div className={styles.chooseSpreadsheet}>
              {selectedSpreadsheet ? (
                <>
                  <div>{selectedSpreadsheet.name}</div>
                  <Button
                    size="sm"
                    color="transparent-no-shadow"
                    onClick={showPicker}
                  >
                    Change
                  </Button>
                </>
              ) : (
                <Button onClick={showPicker}>Choose Spreadsheet</Button>
              )}
            </div>
            <Heading
              size={5}
              className={styles.selectHeading}
              text="Choose a Worksheet"
            />

            {!selectedSheet && (
              <Alert
                className={styles.smallAlert}
                type="warning"
                showIcon
                message="In order to proceed, please create a worksheet in Google Sheets"
              />
            )}

            <Select
              hideCount
              hideClearButton
              singleSelect
              className={styles.dropdown}
              placeholder={
                googleSheetTitle ? googleSheetTitle : 'Choose a Worksheet'
              }
              options={sheetsOptions}
              selected={
                selectedSheet
                  ? [
                      {
                        ...selectedSheet,
                        value: selectedSheet.id,
                        label: selectedSheet.title,
                      },
                    ]
                  : []
              }
              onChange={([v]) => onSelectSheet(v as GoogleSheet)}
              onClose={() => setSearchSheet('')}
              onSearch={v => setSearchSheet(v?.trim() || '')}
              disabled={isReadOnlyAccount || !selectedSpreadsheet}
            />
          </div>
        </div>
      </CollapsibleContentContainer>
      <CollapsibleContentContainer
        ordinal="2"
        title="Look up an existing row"
        color={colors.green7}
      >
        <div className={styles.content}>
          <div className={styles.section}>
            <Heading
              size={5}
              className={styles.heading}
              text="Choose lookup column"
            />

            <div className={styles.description}>
              Search for an existing record using the values in this column
              before writing a new row
            </div>
            <div className={styles.column_select_container}>
              <Select
                hideCount
                hideClearButton
                singleSelect
                className={styles.dropdown}
                placeholder="Choose a column"
                options={columns.map(v => ({ ...v, label: v.name }))}
                selected={
                  selectedLookupColumn
                    ? [
                        {
                          ...selectedLookupColumn,
                          label: selectedLookupColumn.name || '',
                        },
                      ]
                    : []
                }
                onChange={([v]) => onSelectLookupColumn(v)}
                disabled={isReadOnlyAccount}
              />
            </div>
            {!selectedLookupColumn?.id && (
              <Alert
                className={styles.smallAlert}
                type="error"
                showIcon
                icon={<WarningFilled />}
                message="In order to proceed, please create and pre-name columns in a worksheet in Google Sheets"
              />
            )}

            <Button
              color="secondary-blue"
              css={{ container: styles.refresh_button }}
              onClick={onClickRefresh}
              leftIcon={<SyncIcon color="#2f54eb" />}
            >
              Refresh
            </Button>
          </div>
          <div className={styles.section}>
            <Heading size={5} text="Insert Lookup value" />
            <div className={styles.description}>
              Search for the following value in the column above
            </div>

            <Select
              hideCount
              hideClearButton
              singleSelect
              className={classNames(styles.dropdown, styles.textTransformed)}
              placeholder="Choose a column"
              options={columnOptions}
              selected={
                selectedLookupValue
                  ? [
                      {
                        ...selectedLookupValue,
                        value: selectedLookupValue.id,
                        label:
                          attrs.find(o => o.name === selectedLookupValue.name)
                            ?.friendlyName ||
                          normalizeVariableName(selectedLookupValue.name),
                      },
                    ]
                  : []
              }
              onChange={([v]) => onSelectLookupValue(v)}
              onClose={() => setSearchColumn('')}
              onSearch={v => setSearchColumn(v?.trim() || '')}
            />
          </div>
        </div>
      </CollapsibleContentContainer>
      <CollapsibleContentContainer
        ordinal="3"
        title="Update row or create new row with attribute values"
        color={colors.green7}
        noContentLeftBorder
      >
        <div className={styles.content}>
          {mappings?.length ? (
            <div className={styles.mapper_container}>
              <div className={styles.mapper_header}>
                <div className={styles.mapper_header_title}>
                  Google Sheets column
                </div>
                <div className={styles.mapper_header_title}>
                  {companyName} attribute
                </div>
              </div>
              <div className={styles.mapper_rows}>
                {mappings?.map((column, index: number) => (
                  <div
                    className={styles.mapper_row}
                    key={`${column.name}-${index}`}
                  >
                    <div className={styles.mapper_column_name}>
                      {column.sentAs}
                    </div>
                    <span className={styles.mapper_arrow}>
                      <ArrowRightLongIcon />
                    </span>
                    <div className={styles.mapper_attribute}>
                      <PrimarySelect
                        className={styles.mapper_attribute_selector}
                        dropdownClassName={
                          styles.mapper_attribute_selector__dropdown
                        }
                        displayNameKey="friendlyName"
                        options={options.map(v => ({
                          ...v,
                          friendlyName: v.friendlyName,
                        }))}
                        value={attrs.find(({ name }) => name === column.name)}
                        onChange={v =>
                          onAttributeSelected(
                            v as AttributeModel,
                            column.sentAs ?? '',
                            index + 1,
                          )
                        }
                        showSearchFilter
                        customOptionsPosition="top"
                        customDropdownStyles={{ zIndex: '1000003' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <Alert
              className={styles.smallAlert}
              type="warning"
              showIcon
              message="Please fill in previous steps to continue"
            />
          )}
        </div>
      </CollapsibleContentContainer>
    </>
  );
};
